import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';
import axios from 'axios';

import { createReactRenderer } from 'helpers/react-renderer';
import { toBoolean } from 'pages/utils/sanitize';
import { useRemoteDepositPaymentLinkActions } from 'payments/PaymentDepositModal/hooks/useRemoteDepositPaymentLinkActions';
import {
  LSPaymentHardwareSetupStatus,
  useLightspeedPaymentsSetupStatus,
} from 'root/apps/hooks/useLightspeedPaymentsSetupStatus';

import { DangerAlert } from './DangerAlert';
import HoverableCard from './HoverableCard';
import { StyledModal, StyledModalHeader } from './MultiLocationSale.style';
import {
  getResource,
  getTextByTransactionType,
  layawayOrSpecialOrderHandler,
  regularSaleOrQuoteHandler,
} from './utils';
import { WarningAlert } from './WarningAlert';

export const ERROR_MESSAGE =
  'Refresh the page and try again. If the problem persists, contact Support.';
/*
 * transactionId is same as saleID
 * transactionLineId is same as SaleLineID
 *
 * When a saleID is provided get that sale from /API/Account/{AccountID}/Sale/{saleID}?load_relations=["SaleLines"].json
 * if the shopID of that sale is different that the session shopID then its a multi-location sale
 *
 */
const MultiLocationSaleModal = ({
  saleType,
  saleLines = [],
  saleId,
  replaceSaleLine = false,
  saleHasDepositByPaylink,
}: MultiLocationSaleModalProp) => {
  const { id: accountId } = window.merchantos.account;
  const { shopID: sessionShopID } = window.merchantos.session.getShop() ?? {};

  const [modalIsOpen, setModalIsOpen] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [allSaleLines, setAllSaleLines] = React.useState<SaleLineFromApi[]>(saleLines);
  const [shouldMoveSaleLines, setShouldMoveSaleLines] = React.useState<undefined | boolean>(
    undefined
  );
  const onHttpCallError = () => setHasError(true);

  const { status: lightspeedPaymentsSetupStatus } = useLightspeedPaymentsSetupStatus();

  const [currentShopHasLSPay, setCurrentShopHasLSPay] = React.useState(false);
  React.useEffect(() => {
    if (
      lightspeedPaymentsSetupStatus ===
        LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED ||
      lightspeedPaymentsSetupStatus === LSPaymentHardwareSetupStatus.TRANSACTIONAL
    ) {
      setCurrentShopHasLSPay(true);
    } else {
      setCurrentShopHasLSPay(false);
    }
  }, [lightspeedPaymentsSetupStatus]);

  // When saleId is provided fetch that sale and its saleLines
  React.useEffect(() => {
    const getSaleLinesForSale = async () => {
      setIsLoading(true);

      const [data, error] = await getResource({
        requestFunction: () =>
          axios.get(`/API/Account/${accountId}/Sale/${saleId}.json?load_relations=["SaleLines"]`),
      });
      // When data is returned
      if (data) {
        const {
          Sale: { SaleLines },
        } = data;
        const { SaleLine = [] } = SaleLines ?? {};
        if (!Array.isArray(SaleLine)) {
          setAllSaleLines([SaleLine]);
        } else {
          setAllSaleLines(SaleLine);
        }
      }
      if (error) {
        onHttpCallError();
        // eslint-disable-next-line no-console
        console.warn(error);
      }

      setIsLoading(false);
    };

    if (saleId) {
      getSaleLinesForSale();
    }
  }, [accountId, saleId]);

  const { removeCurrentRemoteDepositPaymentRequest } =
    useRemoteDepositPaymentLinkActions(Number(saleId ?? 0)) || {};

  const deleteRemoteDeposit = async () => {
    try {
      if (removeCurrentRemoteDepositPaymentRequest) {
        await removeCurrentRemoteDepositPaymentRequest();
      }
    } catch (error) {
      onHttpCallError();
    }
  };

  const saleLinesToUpdate = allSaleLines.filter(
    (line) => line?.shopID !== sessionShopID && !toBoolean(line?.isWorkorder)
  );

  const closeModal = () => setModalIsOpen(false);

  const handleConfirmClick = async () => {
    setIsLoading(true);

    if (saleHasDepositByPaylink && !currentShopHasLSPay) {
      deleteRemoteDeposit();
    }

    if (!sessionShopID) {
      setHasError(true);
      return;
    }

    const sharedParameters = {
      accountId,
      saleLinesToUpdate,
      sessionShopID,
      shouldMoveSaleLines,
      closeModal,
      onHttpCallError,
    };

    if (saleType === 'regular_sale') {
      await regularSaleOrQuoteHandler({
        saleType: 'regular_sale',
        saleId,
        ...sharedParameters,
      });
    }

    if (saleType === 'quote') {
      await regularSaleOrQuoteHandler({
        saleType: 'quote',
        saleId,
        ...sharedParameters,
      });
    }

    if (saleType === 'layaway') {
      await layawayOrSpecialOrderHandler({
        saleType: 'layaway',
        ...sharedParameters,
        replaceSaleLine: saleLinesToUpdate.length === 1 ? replaceSaleLine : false,
      });
    }

    if (saleType === 'special_order') {
      await layawayOrSpecialOrderHandler({
        saleType: 'special_order',
        ...sharedParameters,
        replaceSaleLine: saleLinesToUpdate.length === 1 ? replaceSaleLine : false,
      });
    }

    setIsLoading(false);
  };

  const { title, recommendedText, nonRecommendedText } = getTextByTransactionType(saleType);

  return (
    <StyledModal isOpen={modalIsOpen}>
      <StyledModalHeader
        data-test="modal-title"
        style={{ borderBottom: '1px solid #d7dce1' }}
        showCloseButton={false}
      >
        {title}
      </StyledModalHeader>
      <ModalBody style={{ paddingTop: '12px' }}>
        {hasError ? (
          <DangerAlert message={ERROR_MESSAGE} title="Something went wrong" noCloseBtn />
        ) : null}
        <Text mb={3}>
          Take the items from your current location to avoid inventory and reporting issues.
        </Text>
        <Flex flexDirection="row" justifyContent="space-between">
          <HoverableCard
            showBadge={true}
            id="recommended-inventory-deduction-option"
            data-test="recommended-deduction-option"
            selected={!hasError && shouldMoveSaleLines === true}
            disableHover={hasError}
            onClick={() => setShouldMoveSaleLines(true)}
            style={{ flexBasis: '50%' }}
            title="Take items from this location"
            badge={{ variant: 'success', text: 'Recommended' }}
            content={recommendedText}
          />
          <HoverableCard
            showBadge={true}
            id="non-recommended-inventory-deduction-option"
            data-test="non-recommended-deduction-option"
            selected={!hasError && shouldMoveSaleLines === false}
            disableHover={hasError}
            onClick={() => setShouldMoveSaleLines(false)}
            style={{ flexBasis: '50%', marginLeft: '2%' }}
            title="Take items from other location(s)"
            content={nonRecommendedText}
          />
        </Flex>
        {saleHasDepositByPaylink && !currentShopHasLSPay ? (
          <WarningAlert
            message="This current location is not integrated with Lightspeed Payments. Open the sale from its original location to benefit from the remote deposit feature and generate a payment link. "
            title="A remote deposit will be removed from the sale"
            noCloseBtn
          />
        ) : null}
      </ModalBody>
      <ModalFooter style={{ backgroundColor: '#F2F5F8', borderTop: '1px solid #D7DCE1' }}>
        <Flex flexDirection="row-reverse">
          <Button
            id="confirm-selection-button"
            fill
            loading={isLoading}
            data-test={`confirm-button-${isLoading ? 'loading' : 'ready'}`}
            variant="secondary"
            disabled={isLoading || hasError || typeof shouldMoveSaleLines === 'undefined'}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

/**
 * Examples
 * For regular sale/quotes
 *
 * startMultiLocationSale({ saleType: 'regular_sale', saleId: '145' })
 * startMultiLocationSale({ saleType: 'quote', saleId: '4566' })
 *
 * For layaways/SO
 *
 * startMultiLocationSale({ saleType: 'layaway', saleLines: [{saleLineID: '2345'}, {saleLineID: '1234'}] })
 * startMultiLocationSale({ saleType: 'special_order', saleLines: [{saleLineID: '2345'}] })
 *
 */
export const startMultiLocationSale = (props: MultiLocationSaleModalProp) => {
  const RenderedComponent = createReactRenderer({
    component: MultiLocationSaleModal,
    selector: '[data-react-root="ReactModal"]',
  });
  RenderedComponent.mount({
    props: { ...props },
  });
};

export default MultiLocationSaleModal;
